import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { ConfirmDialog } from '@repo/shared-client/components/blocks'
import {
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { useTranslation } from '@web/src/lib/intl/react'
import {
  CheckIcon,
  CircleXIcon,
  ClockIcon,
  LogOutIcon,
  MailQuestionIcon,
  MessageCircleQuestionIcon,
  UserPlusIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import EventAttendeeSignupDialog, {
  UPDATE_ATTENDANCE_STATUS,
} from 'src/features/event/event-attendee-signup-dialog/event-attendee-signup-dialog'
import type { EventAttendanceStatus, EventJoinRequestStatus, EventRole, EventVisibility } from 'types/graphql'

export const EVENT_JOIN_REQUEST_CANCEL = gql`
  mutation EventJoinRequestCancel($id: String!) {
    eventJoinRequestCancel(id: $id)
  }
`

export const LEAVE_EVENT = gql`
  mutation LeaveEvent($eventIdOrSlug: String!) {
    leaveEvent(eventIdOrSlug: $eventIdOrSlug) {
      id
    }
  }
`

interface EventAttendanceProps {
  event: {
    id?: string
    slug?: string
    name?: string
    ownerId?: string
    visibility?: EventVisibility
    attendeeApproval?: boolean
    myAttendance?: {
      status?: EventAttendanceStatus
      role?: EventRole
    }
    myJoinRequest?: {
      id?: string
      status?: EventJoinRequestStatus
    }
    myInvitation?: {
      id?: string
    }
  }
  onStatusChange?: () => void
}

const EventAttendance = ({ event, onStatusChange }: EventAttendanceProps) => {
  const { t } = useTranslation()
  const [isSignupDialogOpen, setIsSignupDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'request' | 'going'>('request')
  const [showCancelRequestModal, setCancelRequestModal] = useState(false)

  const [updateAttendanceStatus, { loading: updateLoading }] = useMutation(UPDATE_ATTENDANCE_STATUS, {
    onCompleted: () => {
      toast.success(t('Attendance status updated'))
      if (onStatusChange) {
        onStatusChange()
      }
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [cancelRequest, { loading: cancelLoading }] = useMutation(EVENT_JOIN_REQUEST_CANCEL, {
    onCompleted: () => {
      toast.success(t('Join request cancelled'))
      if (onStatusChange) {
        onStatusChange()
      }
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [leaveEvent, { loading: leaveLoading }] = useMutation(LEAVE_EVENT, {
    onCompleted: () => {
      toast.success(t('You have left the event'))
      if (onStatusChange) {
        onStatusChange()
      }
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const handleStatusChange = async (value: string) => {
    if (value === event?.myAttendance?.status) return

    if (value === 'LEAVE') {
      await handleLeaveEvent()
      return
    }

    if (value === 'GOING') {
      setDialogMode('going')
      setIsSignupDialogOpen(true)
      return
    }

    try {
      await updateAttendanceStatus({
        variables: {
          eventIdOrSlug: event?.slug || event?.id,
          status: value,
        },
      })
    } catch (error) {
      console.error('Error updating attendance status:', error)
    }
  }

  const handleOpenSignupDialog = (mode: 'request' | 'going') => {
    setDialogMode(mode)
    setIsSignupDialogOpen(true)
  }

  const handleCancelRequest = async () => {
    try {
      await cancelRequest({ variables: { id: event?.myJoinRequest?.id } })
    } catch (error) {
      console.error('Error cancelling request:', error)
    }
  }

  const handleLeaveEvent = async () => {
    try {
      await leaveEvent({ variables: { eventIdOrSlug: event?.slug || event?.id } })
    } catch (error) {
      console.error('Error leaving event:', error)
    }
  }

  const handleCancelRequestEvent = async () => {
    setCancelRequestModal(false)
    try {
      await updateAttendanceStatus({
        variables: {
          eventIdOrSlug: event?.slug || event?.id,
          status: 'NOTGOING',
        },
      })
    } catch (error) {
      console.error('Error canceling request:', error)
    }
  }

  const getStatusIcon = (status: EventAttendanceStatus) => {
    switch (status) {
      case 'GOING':
        return <CheckIcon className="size-4 shrink-0" />
      case 'MAYBE':
        return <ClockIcon className="size-4 shrink-0" />
      case 'NOTGOING':
        return <XIcon className="size-4 shrink-0" />
      case 'WAITLISTED':
        return <UsersIcon className="size-4 shrink-0" />
      case 'NONE':
        return <MessageCircleQuestionIcon className="size-4 shrink-0" />
      default:
        return <MailQuestionIcon className="size-4 shrink-0" />
    }
  }

  const getStatusButtonClassName = (status?: EventAttendanceStatus) => {
    switch (status) {
      case 'GOING':
        return 'bg-green-600 text-white hover:bg-green-600/90'
      case 'MAYBE':
        return 'bg-amber-500 text-black hover:bg-amber-500/90'
      case 'NOTGOING':
        return 'bg-primary text-primary-foreground hover:bg-primary/90'
      case 'WAITLISTED':
        return 'bg-purple-600 text-white hover:bg-purple-600/90'
      default:
        return 'bg-secondary text-secondary-foreground'
    }
  }

  const getStatusText = (status: EventAttendanceStatus) => {
    switch (status) {
      case 'GOING':
        return t('Going')
      case 'MAYBE':
        return t('Maybe')
      case 'NOTGOING':
        return t('Not Going')
      case 'WAITLISTED':
        return t('Waitlisted')
      case 'NONE':
        return t('Respond')
      default:
        return t('Respond')
    }
  }

  const hasAttendeeRecord = !!event?.myAttendance
  const showJoinRequest =
    event?.attendeeApproval &&
    !hasAttendeeRecord &&
    (!event?.myJoinRequest || event?.myJoinRequest?.status === 'REJECTED')
  const showJoinRequestCancel =
    event?.attendeeApproval && event?.myJoinRequest && event?.myJoinRequest?.status === 'PENDING'
  const showWaitlisted = hasAttendeeRecord && event?.myAttendance?.status === 'WAITLISTED'
  const showAttendanceMenu = !(showJoinRequest || showJoinRequestCancel || showWaitlisted)

  const menuLoading = updateLoading || leaveLoading

  return (
    <>
      <Card className="w-full px-2 py-2">
        <CardContent className="p-0">
          <div className="flex w-full flex-wrap items-center justify-between gap-4 md:flex-nowrap">
            <div className="flex w-full flex-wrap items-center justify-center gap-2 md:flex-nowrap md:justify-end">
              {!!showJoinRequest && (
                <Button
                  aria-label={t('Request Access')}
                  className="gap-1.5"
                  onClick={() => handleOpenSignupDialog('request')}
                  type="button"
                  variant="default"
                >
                  <UserPlusIcon className="size-4 shrink-0" />
                  {t('Request Event Access')}
                </Button>
              )}

              {!!showJoinRequestCancel && (
                <div className="flex flex-col items-stretch gap-1">
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <Button
                      aria-label={t('Request Pending')}
                      className="gap-1.5"
                      disabled
                      type="button"
                      variant="secondary"
                    >
                      <ClockIcon className="size-4 shrink-0" />
                      {t('Request Pending')}
                    </Button>
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <Button
                            aria-label={t('Cancel Request')}
                            className="gap-1.5"
                            disabled={cancelLoading}
                            onClick={handleCancelRequest}
                            type="button"
                            variant="outline"
                          >
                            <CircleXIcon className="size-4 shrink-0" />
                            {t('Cancel')}
                          </Button>
                        }
                      />
                      <TooltipContent>{t('Cancel your request')}</TooltipContent>
                    </Tooltip>
                  </div>
                  <p className="text-center text-muted-foreground text-xs">{t('Your request is being reviewed')}</p>
                </div>
              )}

              {!!showWaitlisted && (
                <div className="flex flex-wrap items-start justify-center gap-4">
                  <div className="flex flex-col items-stretch gap-1">
                    <Button
                      aria-label={t('Waitlisted')}
                      className="gap-1.5"
                      disabled
                      size="sm"
                      type="button"
                      variant="secondary"
                    >
                      <UsersIcon className="size-4 shrink-0" />
                      {t('On Wait List')}
                    </Button>
                    <p className="text-center text-muted-foreground text-xs">
                      {t('You are on the waitlist for this event')}
                    </p>
                  </div>

                  <div className="flex flex-col items-stretch gap-1">
                    <Button
                      aria-label={t('Cancel waitlist request')}
                      className="gap-1.5"
                      onClick={() => setCancelRequestModal(true)}
                      size="sm"
                      type="button"
                      variant="outline"
                    >
                      <XIcon className="size-4 shrink-0" />
                      {t('Cancel')}
                    </Button>
                    <p className="text-center text-muted-foreground text-xs">{t('Cancel Request')}</p>
                  </div>
                </div>
              )}

              {!!showAttendanceMenu && (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <Button
                        aria-label={t('Attendance status')}
                        className={cn('gap-1.5', getStatusButtonClassName(event?.myAttendance?.status))}
                        disabled={menuLoading}
                        type="button"
                        variant="secondary"
                      >
                        {menuLoading ? (
                          <Spinner className="size-4" />
                        ) : (
                          getStatusIcon(event?.myAttendance?.status ?? 'NONE')
                        )}
                        {getStatusText(event?.myAttendance?.status ?? 'NONE')}
                      </Button>
                    }
                  />
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      disabled={event?.myAttendance?.status === 'GOING'}
                      onClick={() => {
                        handleStatusChange('GOING').catch(console.error)
                      }}
                    >
                      <span className="flex items-center gap-2">
                        {getStatusIcon('GOING')}
                        {getStatusText('GOING')}
                      </span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={event?.myAttendance?.status === 'MAYBE'}
                      onClick={() => {
                        handleStatusChange('MAYBE').catch(console.error)
                      }}
                    >
                      <span className="flex items-center gap-2">
                        {getStatusIcon('MAYBE')}
                        {getStatusText('MAYBE')}
                      </span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={event?.myAttendance?.status === 'NOTGOING'}
                      onClick={() => {
                        handleStatusChange('NOTGOING').catch(console.error)
                      }}
                    >
                      <span className="flex items-center gap-2">
                        {getStatusIcon('NOTGOING')}
                        {getStatusText('NOTGOING')}
                      </span>
                    </DropdownMenuItem>
                    {!!event?.myAttendance && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => {
                            handleStatusChange('LEAVE').catch(console.error)
                          }}
                        >
                          <span className="flex items-center gap-2">
                            <LogOutIcon className="size-4 shrink-0" />
                            {t('Leave Event')}
                          </span>
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <EventAttendeeSignupDialog
        event={event}
        mode={dialogMode}
        onOpenChange={({ open }) => setIsSignupDialogOpen(open)}
        onRequestComplete={onStatusChange}
        open={isSignupDialogOpen}
      />

      <ConfirmDialog
        cancelText={t('No')}
        confirmText={t('Yes')}
        onConfirm={handleCancelRequestEvent}
        onOpenChange={(e) => setCancelRequestModal(e.open)}
        open={showCancelRequestModal}
        text={t(
          'Are you sure you want to cancel your request? You will loose your spot on the waitlist for this event.'
        )}
        title={t('Cancel Request')}
        variant="basic"
      />
    </>
  )
}

export default EventAttendance
